import { useEffect, useRef, useState } from 'react';
import { X, Share, MoreVertical, Download } from 'lucide-react';
import {
  canInstall,
  detectPlatform,
  isMobile,
  isStandalone,
  promptInstall,
  subscribeInstall,
  type Platform,
} from '@/utils/installPrompt';

const LS_KEY = 'opex_a2hs_shown';

/**
 * 「添加到桌面」引导：登录后首次进入首页弹一次。
 * - 安卓(Chrome)：共享单例捕获 beforeinstallprompt，可一键触发系统安装横幅
 * - 苹果(iOS Safari)：不支持自动安装，展示「分享→添加到主屏幕」图文步骤
 * - 已安装(standalone)或曾提示过(localStorage)则永不再出现
 *
 * 安装能力(beforeinstallprompt)统一由 @/utils/installPrompt 单例捕获，
 * 与右上角常驻 InstallButton 共享，避免双重监听互相抢占。
 */
export default function AddToHomeScreen({ active }: { active: boolean }) {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<Platform>('other');
  const [canPrompt, setCanPrompt] = useState(false);
  const shownRef = useRef(false);

  // 跟踪安装能力状态（是否可一键安装 / 是否已安装）
  useEffect(() => {
    const refresh = () => {
      setCanPrompt(canInstall());
      if (isStandalone()) {
        localStorage.setItem(LS_KEY, '1');
        setVisible(false);
      }
    };
    refresh();
    return subscribeInstall(refresh);
  }, []);

  // 进入首页(登录后主界面)时弹一次
  useEffect(() => {
    if (!active || shownRef.current) return;
    if (isStandalone()) return;
    if (localStorage.getItem(LS_KEY)) return;
    if (!isMobile()) return;
    shownRef.current = true;
    localStorage.setItem(LS_KEY, '1');
    setPlatform(detectPlatform());
    const t = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(t);
  }, [active]);

  const dismiss = () => setVisible(false);

  const install = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={dismiss} />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-xl">
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 p-1.5 rounded-full text-gray-400 active:bg-gray-100"
          aria-label="关闭"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <img src="/icons/icon-192.png" alt="OPEX营策" className="w-12 h-12 rounded-xl shadow-sm" />
          <div>
            <h3 className="text-base font-semibold text-gray-900">添加到桌面</h3>
            <p className="text-xs text-gray-500 mt-0.5">像 App 一样，从桌面图标一键打开</p>
          </div>
        </div>

        {platform === 'ios' && (
          <ol className="space-y-2.5 text-sm text-gray-700">
            <li className="flex items-center gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center">1</span>
              点 Safari 底部 <Share className="w-4 h-4 text-blue-600" /> 分享按钮
            </li>
            <li className="flex items-center gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center">2</span>
              下滑选择「添加到主屏幕」
            </li>
            <li className="flex items-center gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center">3</span>
              点右上角「添加」确认
            </li>
          </ol>
        )}

        {platform === 'android' && (
          <ol className="space-y-2.5 text-sm text-gray-700">
            {!canPrompt && (
              <li className="flex items-center gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center">1</span>
                点浏览器右上 <MoreVertical className="w-4 h-4 text-blue-600" /> 菜单
              </li>
            )}
            <li className="flex items-center gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center">{canPrompt ? '1' : '2'}</span>
              选择「添加到主屏幕」或「安装应用」
            </li>
            <li className="flex items-center gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center">{canPrompt ? '2' : '3'}</span>
              确认添加即可
            </li>
          </ol>
        )}

        {platform === 'other' && (
          <p className="text-sm text-gray-700">
            点浏览器地址栏右侧的安装图标，或在菜单中选择「安装应用 / 创建快捷方式」，即可添加到桌面。
          </p>
        )}

        <div className="mt-5 flex gap-2">
          {canPrompt && (
            <button
              onClick={install}
              className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 text-white py-3 rounded-xl font-medium active:bg-blue-700"
            >
              <Download className="w-4 h-4" /> 立即安装
            </button>
          )}
          <button
            onClick={dismiss}
            className={`${canPrompt ? '' : 'flex-1 '}py-3 px-4 rounded-xl font-medium text-gray-600 bg-gray-100 active:bg-gray-200`}
          >
            {canPrompt ? '以后再说' : '知道了'}
          </button>
        </div>
      </div>
    </div>
  );
}
